CompilerSettings
	serializeInternal	= false
	serializeExternal	= false
	stateVariables	= true

Config
	Field[]	fields

// Common styles used throughout the app. If a style is used in multiple places, define it here.
global style
	global Color	primaryColor	= Color.hsl(34, 94.80%, 54.90%)
	global Color	secondaryColor	= #0098da
	global Color	lightColor	= Color.hsl(34, 95%, 95%)
	global Color	lightGray	= Color.hsl(0, 0%, 50%)
	global Color	veryLightGray	= Color.hsl(0, 0.00%, 94.90%)
	global Color 	okColor	= #16C66E
	global Color	failedColor	= #C01352

	const Dimension	boxBorderRadius = 20px
	const BoxShadow	boxShadow = {offset:{10,10}, blur:40, spread:0, color:#d0d0d0}
	const Dimension	gapBetweenBoxes = 24px

struct float
	private const NumberFormat	currency = {"de-DE", {style:currency, currency:"EUR", maximumFractionDigits:0}}
	inline string	currencyString := currency.format(this)

	private const NumberFormat	currencyWithCents = {"de-DE", {style:currency, currency:"EUR", maximumFractionDigits:2}}
	inline string	currencyWithCentsString := currencyWithCents.format(this)

	private const NumberFormat	pecentage = {"de-DE", {maximumFractionDigits:2}}
	inline string	percentageString := pecentage.format(this * 100) + " %"

app
	const Page[]	pagesInHeader = [buyPage, myPage, contactPage]
	const int	minimumBidIncrease = 100
	const string	titleSuffix = " - 123womo"

	bool	showNotifications = false
	bool	showCookieBanner = true
	const string	cookieBannerKey = "cookieAccepted"
	string	selectedColor = ""
	string	selectedColor2 = ""
	Config	config

	Page	currentPage = null
	User	user = null
	float	serverTimeOffset = 0

	// Database tables
	DatabaseTable<Field>	fields	= {name:"fields"}
	DatabaseTable<Good>	goods	= {name:"goods"}
	DatabaseTable<Text>	texts	= {name:"texts"}
	DatabaseTable<Action>	actions	= {name:"actions"}
	DatabaseTable<Message>	messages	= {name:"messages"}

	// Text
	Text[string]	textMap	= {}
	string	language	= "german"

	string[GoodState]	goodStateNames = {
		Guest:	"Entwurf",
		Draft:	"Entwurf",
		Review:	"Wird geprüft",
		Auction:	"Auktion läuft",
		Negotiation:	"Nachverhandlung",
		NotSold:	"Nicht verkauft",
		Sold:	"Verkauft"
	}

	string[UserType]	userTypeNames = {
		PrivateSeller:	"Privater Verkäufer",
		BusinessSeller:	"Gewerblicher Verkäufer",
		Business:	"Händler"
	}

	init
		Platform.referenceSizes = []
		Platform.ticksPerSecond = 1
	
	start
		Loca.setLanguage German
		let cookieValue = Platform.getLocalStorageString(cookieBannerKey)
		showCookieBanner = cookieValue != "1"
		await fetchTexts
		backend.autoCancellation false
		await fields.fetchAll
		user = (await backend.getAuthenticatedUser) or (await createGuest)
		onPopState: setPageFromUrl
		await setPageFromUrl
		refresh

		onLogin
		await syncServerTime
	
	tick
		currentPage?.tick

	onLogin
		messages.unsubscribe
		if user.isGuest: return
		await messages.subscribe "user='{user.id}'"
			messages.items.orderBy.created
			if currentPage == auctionPage
				auctionPage.onMessagesChanged

	async syncServerTime
		// Do 3 NTP measurements and use the one with lowest RTT for best accuracy
		print "Starting NTP sync..."
		try
			float bestOffset = 0
			float lowestRTT = float.maxValue
			for 3
				float t0 = Time.now
				let response = await http.get "{backend.url}/api/time"
				float t3 = Time.now
				float rtt = t3 - t0
				float offset = response.time - (t0 + t3) / 2
				print "NTP sync {i+1}: offset={offset}ms, RTT={rtt}ms"
				if rtt < lowestRTT
					lowestRTT = rtt
					bestOffset = offset
			serverTimeOffset = bestOffset
			print "Best server time offset (RTT={lowestRTT}ms):", serverTimeOffset, "ms"
		catch as error
			print "NTP sync error:", error
			serverTimeOffset = 0

	bool isLoggedIn := user && user.id

	logout
		backend.logout
		user = await createGuest
		homePage.good = createDefaultGood Guest
		navigateTo homePage	

	Good createDefaultGood: GoodState state = Guest
		return {
			Typ: "Wohnmobil"
			state: state
			seller: user.id
			readyForPickupTime: TimeString.now
			Umsatzsteuer: user.userType == PrivateSeller ? "nicht umsatzsteuerpflichtig" :"inkl. 19% Umsatzsteuer"
			PLZ: user.zipCode
			Ort: user.city
			Land: user.country
			bids: []
		}	

	private createGuest
		let name = math.randomString(12)
		let password = math.randomString(24)
		user = {isGuest:true, userType:PrivateSeller, email:"{name}@guest.123womo.de", password:password, passwordConfirm:password}
		return await backend.signupNewUser user

	private setPageFromUrl
		dynamic page = homePage
		let slashIndex = Platform.locationHash.indexOf("/")
		if slashIndex == -1: slashIndex = Platform.locationHash.length
		let firstSegment = Platform.locationHash[1..slashIndex]
			page = globals[firstSegment+"Page"]
			if !page?.show and textMap[firstSegment+"Page"]: page = staticPage
		if !page?.show: page = homePage
		currentPage?.hide
		currentPage = page
		await page.show(Platform.locationHash[slashIndex+1..])
		refresh

	navigateTo: Page page, string id = ""
		Platform.pushState "#{page.type.toString[..-4].toLowerCase}/{id}"
		currentPage?.hide
		currentPage = page
		await page.show id
		refresh
		html.scrollToTop

	private fetchTexts
		await texts.fetchAll
		for texts: textMap[.kind] = .

	string getText: string kind
		let text = textMap[kind]
		return text ? text[language+"Text"] : ""

	saveFields
		print "Loading fields from yaml"
		await yaml.setup
		config = yaml.parse(media.config)
		print "Saving fields to database"
		for config.fields as field
			field.editOrder = field.viewOrder = (i+1)*10
			let existingField = await fields.fetchOne filter:"name='{field.name}'"
			if existingField
				print "{i}: Updating {field.name}"
				await fields.update existingField, field
			else
				print "{i}: Adding {field.name}"
				await fields.add field
		print "Fields saved to database"

	draw
		// html.style "* \{ user-select: none; -webkit-user-select: none; \}"
		html.style "h1" color:primaryColor
		html.style "h2" color:primaryColor margin:{top:24 bottom:0}
		html.style "p" margin:{}
		html.style "details", "background: #ffffff; border: 1px solid #e5e7eb; border-radius: 16px; padding: 18px 22px; margin-bottom: 16px; box-shadow: 0 10px 24px rgba(0,0,0,0.04);"
		html.style "summary", "cursor: pointer; font-size: 18px; font-weight: 600; color: #111827; list-style: none;"
		html.style "summary::-webkit-details-marker", "display: none;"
		html.style "details[open] summary:after", "content: '-'; float: right; font-size: 18px; font-weight: 400; color:"+primaryColor+";"
		html.style "details summary:after", "content: '+'; float: right; font-size: 18px; font-weight: 400; color:" +primaryColor+";"
		html.style "details p", "margin-top: 12px; margin-bottom: 0; color: #4b5563; line-height: 1.5;"
		if currentPage
			div width:960px, margin:{leftAndRight:auto}
				if currentPage.hasHeaderAndFooter: drawHeader
				currentPage.draw
				if currentPage.hasHeaderAndFooter: drawFooter
		if showCookieBanner: drawCookieBanner

	drawHeader
		div borderBottom:{width:1px, style:solid, color:#e5e9f2}, width:100%
			div width:100%, padding:{top:20 bottom:20}, display:flex, alignItems:center, justifyContent:spaceBetween
				img Logo, width:200, cursor:pointer, onClick:navigateTo homePage
				div display:flex, gap:28, fontSize:16, color:#40465a, fontWeight:bold
					let pagesToShow = pagesInHeader.where not .showFor or (user and not user.isGuest and .showFor.contains(user.userType))
					contactPage.title = pagesToShow.length <= 1 ? "Fragen? Wir sind für Sie da!" : "Kontakt"
					for pagesToShow as page
						div text:page.title, color:currentPage == page ? primaryColor : #40465a, cursor:pointer
							onClick:navigateTo page
				if user
					if user.isGuest
						app.button text:"Anmelden", onClick:navigateTo loginPage
					else
						div display:flex, alignItems:center, gap:20
							div display:flex, alignItems:center, gap:8, backgroundColor:lightColor, padding:{top:6 bottom:6 left:12 right:12}, borderRadius:style.boxBorderRadius, cursor:pointer
								div text:"👤", fontSize:14, opacity:.8, color:lightGray
								div text:user.name or user.email or "Mein Account", fontSize:14, color:lightGray, cursor:pointer, onClick:navigateTo accountPage
							drawNotifications

	drawFooter
		row alignItems:center justifyContent:center margin:{top:40 bottom:100} gap:16
			html.link "Impressum" to "/#imprint"
			html.link "Datenschutz" to "/#privacy"
			html.link "AGB" to "/#agb"

	drawNotifications
		let unreadMessages = messages.where.read == false
		let unreadCount = unreadMessages.length
		div position:relative
			// Bell icon with badge
			div cursor:pointer, padding:{top:6 bottom:6 left:8 right:8}, onClick:showNotifications = !showNotifications; refresh
				div position:relative, display:inlineBlock
					img bell, width:32, marginTop:8
					if unreadCount > 0
						div position:absolute, top:-6, right:-8, backgroundColor:#e53935, color:white, fontSize:11, fontWeight:bold
							minWidth:18, height:18, borderRadius:9, display:flex, alignItems:center, justifyContent:center
							text:unreadCount > 99 ? "99+" : "{unreadCount}"
			// Popup
			if showNotifications
				// Click-outside overlay
				div position:fixed, top:0, left:0, right:0, bottom:0, zIndex:999, onClick:showNotifications = false; refresh
				// Notification popup
				div position:absolute, right:0, top:40, zIndex:1000, width:360, maxHeight:600, overflowY:auto
					backgroundColor:white, borderRadius:style.boxBorderRadius, boxShadow:style.boxShadow, border:{1px solid #e5e9f2}
					if messages.length == 0
						div text:"Keine Benachrichtigungen", padding:{top:24 bottom:24 left:16 right:16}, color:lightGray, textAlign:center
					else
						div padding:{top:12 bottom:12 left:16 right:16}, borderBottom:{1px solid #e5e9f2}
							div text:"Benachrichtigungen", fontSize:16, fontWeight:bold, color:#2b3145
						for messages backwards as message
							drawNotificationItem message

	drawNotificationItem: Message message
		row padding:{top:12 bottom:12 left:16 right:16}, cursor:pointer
			backgroundColor:message.read ? white : style.lightColor
			borderBottom:{1px solid #e5e9f2}, gap:12
			onClick:
				showNotifications = false
				if message.good: navigateTo auctionPage, message.good
				else             refresh
				if not message.read: await messages.update message, {read:true}
					
			// Unread indicator
			div width:8, height:8, borderRadius:4, marginTop:6, flexShrink:0
				backgroundColor:message.read ? transparent : primaryColor

			if message.imageUrl
				img src:"{backend.url}{message.imageUrl}", width:48, height:48, objectFit:cover, borderRadius:8, flexShrink:0

			column gap:4
				if message.goodName: div text:message.goodName, fontSize:12
				if message.subject: div text:message.subject, fontSize:15, fontWeight:bold, color:secondaryColor
				// if message.price: div text:message.price.currencyString, fontSize:28, fontWeight:bold, color:secondaryColor
				div text:message.text, fontSize:14, color:#2b3145
				div text:"{message.created.dayShortMonthNameString} {message.created.hourMinuteString} Uhr", fontSize:12, color:lightGray

	drawCookieBanner
		div position:fixed, left:0, right:0, bottom:0, zIndex:999
			backgroundColor:#f0f0f0	, color:#2b3145, fontSize:12, padding:{top:24 bottom:24 left:24 right:24}
			display:flex, alignItems:center, gap:12
			div getText("cookieBanner")
			html.get("acceptCookiesButton").onclick = app.clickCookieBanner
	
	clickCookieBanner
		Platform.setLocalStorageString cookieBannerKey, "1"
		showCookieBanner = false
		print "Cookie banner accepted"
		refresh

	box: Dimension width = 100%, void() children
		div width, backgroundColor:white, borderRadius:style.boxBorderRadius, padding:{top:32 bottom:32 left:32 right:32}, boxShadow:style.boxShadow
			children

	button: string text, void() onClick = null, bool isDisabled = false, bool triggerWithEnterKey = false, Dimension width = auto, Dimension height = auto
		html.button text:text, fontSize:16, padding:{top:12 bottom:12 left:32 right:32}, triggerWithEnterKey:triggerWithEnterKey
			borderRadius:8, border:{2px solid isDisabled ? #d1d5db : transparent}, backgroundColor:isDisabled ? white : primaryColor
			color:isDisabled ? #9aa2b1 : white, cursor:isDisabled ? default : pointer, onClick:if(!isDisabled) onClick()
			width, height

	drawTags: Good item
		div display:flex, flexWrap:wrap, gap:10
			if item.Jahr_der_Erstzulassung	drawTag "Erstzulassung: {item.Jahr_der_Erstzulassung}"
			if item.Kilometerstand	drawTag "{item.Kilometerstand} km"
			if item.Motorleistung_in_kW	drawTag "{item.Motorleistung_in_kW} kW"
			if item.Getriebe	drawTag item.Getriebe
			if item.Fahrgestell	drawTag item.Fahrgestell
			if item.Anzahl_der_Schlafplaetze	drawTag "{item.Anzahl_der_Schlafplaetze} Schlafplätze"

	drawTag: string text
		div text:text, fontSize:13, color:#465068, backgroundColor:#edf1f7, padding:{top:6 bottom:6 left:10 right:10}, borderRadius:14

	drawGoodBox: Good good
		column backgroundColor:#ffffff, borderRadius:20, padding:{top:24 bottom:24 left:24 right:24}, boxShadow:{offset:{0,10}, blur:30, spread:0, color:#dfe4ef}, cursor:pointer, gap:12
			let msg = app.messages.items.findLast.good == good.id
				if !(good.state == Auction and msg.context == "Prepare")
					switch msg.category
						Ok:	div text:msg.subject, fontSize:13, color:white, backgroundColor:style.okColor, padding:{top:6 bottom:6 left:10 right:10}, borderRadius:14
						Failed:	div text:msg.subject, fontSize:13, color:white, backgroundColor:style.failedColor, padding:{top:6 bottom:6 left:10 right:10}, borderRadius:14
						ActionRequired:	div text:msg.subject, fontSize:13, color:white, backgroundColor:style.primaryColor, padding:{top:6 bottom:6 left:10 right:10}, borderRadius:14
			row gap:20
				div display:flex, flexDirection:column, alignItems:center, gap:16
					if good.Bilder and good.Bilder.length > 0
						img "{backend.url}/api/files/goods/{good.id}/{good.Bilder[0]}", width:220, height:150, objectFit:cover, borderRadius:18
					else
						img Placeholder, width:220, height:150, objectFit:cover, borderRadius:18
					if good.state == Auction
						div "Standort: {good.Ort}" fontSize:14, color:#7a8195

				div flex:1, display:flex, flexDirection:column, gap:18
					div "{good.Marke} {good.Modell}" fontSize:25, color:#2b3145
					// div text:app.goodStateNames[good.state], fontSize:13, color:white, backgroundColor:primaryColor, padding:{top:6 bottom:6 left:10 right:10}, borderRadius:14
					if good.state == Auction
						drawCountdown good
						row gap:32
							if app.user?.canSeeAuction
								drawPrice "Aktueller Preis", good.currentPrice.currencyString or "Kein Gebot", good.Umsatzsteuer
							drawPrice "Preisvorstellung des Verkäufers", good.Preisvorstellung ? good.Preisvorstellung.currencyString : "Keine Preisvorstellung", good.Umsatzsteuer
					else
						app.drawTags good

			if good.state == Auction
				app.drawTags good

		onClick:
			if good.seller == user.id and (good.state == Guest or good.state == Draft)
				app.navigateTo editPage, good.id
			else
				app.navigateTo auctionPage, good.id
			

	drawPrice: string label, string value, string sublabel
		div display:flex, flexDirection:column
			div text:label, fontSize:14, color:#7a8195
			div text:value, fontSize:22, fontWeight:bold, color:secondaryColor
			// if sublabel != "": div text:sublabel, fontSize:13, color:#9aa2b1

	drawCountdown: Good good
		if not good.auctionEndTime
			div marginTop:16
				div text:"Auktionsende nicht festgelegt", fontSize:13, color:lightGray
			return
		// Calculate remaining time using server time offset
		float serverTime = Time.now + app.serverTimeOffset
		float endTime = Date(good.auctionEndTime).time
		float remaining = endTime - serverTime
		if remaining <= 0
			div text:"Auktion beendet am {good.auctionEndTime.dayOfWeekDayMonthString} um {good.auctionEndTime.hourMinuteString} Uhr", fontSize:13, color:lightGray
		else
			// Calculate days, hours, minutes, seconds
			int totalSeconds = math.ceil(remaining / 1000)
			// int days = math.floor(totalSeconds / 86400)
			int hours = math.floor(totalSeconds / 3600)
			int minutes = math.floor((totalSeconds mod 3600) / 60)
			int seconds = totalSeconds mod 60
			// Format countdown string
			string countdownText = ""
			// if days > 0: countdownText = "{days} Tag{days != 1 ? "e" : ""} "
			if hours > 0: countdownText = countdownText + "{hours} Stunde{hours != 1 ? "n" : ""} "
			if minutes > 0 or hours > 0: countdownText = countdownText + "{minutes} Minute{minutes != 1 ? "n" : ""} "
			countdownText = countdownText + "{seconds} Sekunde{seconds != 1 ? "n" : ""}"
			div
				div text:"Auktion endet am {good.auctionEndTime.dayOfWeekDayMonthString} um {good.auctionEndTime.hourMinuteString} Uhr", fontSize:13, color:lightGray
				div text:countdownText, fontSize:18, fontWeight:bold

Page
	string title
	bool hasHeaderAndFooter = true
	UserType[] showFor = []
	async show: string id = ""
	hide
	draw
	tick
